import { ref } from 'vue'
import { useEventBus } from '@vueuse/core'
import { logger } from '@sentry/vue'
import { getApiLink } from '@/utils/api-urls'
import type { Mode } from '@/utils/api-urls'
import useEmbed from '@/embed/code'
import { WebSocketConnectionError } from './connection-error'
import { isForbidden, isScanResult } from './classify'
import { createServerEventStore } from './event-store'
import type { Message } from './frames'

export enum WEBSOCKET_STATUS {
    connecting,
    open,
    closing,
    closed,
}

const MAX_ATTEMPTS = 5
const WEBSOCKET_CLOSED_CODE = 1000

export const createWebSocketTransport = () => {
    const store = createServerEventStore()
    const websocketInstance = ref<WebSocket | undefined>()

    let timer_id: NodeJS.Timeout | undefined
    let pingTimerId: NodeJS.Timeout | undefined
    let attempts = 0
    let generation = 0

    const handleWebsocketMessage = (m: MessageEvent) => {
        const payload: unknown = JSON.parse(m.data)

        useEventBus('websocket-message').emit(payload)

        // prevent further handling "forbidden" in response from healthcheck
        if (isForbidden(payload)) {
            return
        }

        if (!store.tasks.value.length) {
            store.setFirstTask(payload)
            useEmbed().setWorkflowExecutionPhase('running')
        }

        if (isScanResult(payload)) {
            return
        }

        store.append(payload as Message)
    }

    const ping = () => {
        const pingGeneration = generation

        const sendPing = () => {
            if (pingGeneration !== generation) {
                return
            }

            if (!websocketInstance.value || websocketInstance.value.readyState !== WEBSOCKET_STATUS.open) {
                return
            }

            websocketInstance.value.send('ping')
            pingTimerId = setTimeout(sendPing, 15000)
        }

        sendPing()
    }

    const initialize = (token: string, workflowId: string, mode: Mode) => new Promise<void>((resolve, reject) => {
        const socketGeneration = generation
        const isCurrentSocket = () => socketGeneration === generation

        const socketUrl = getApiLink('ws', mode)
        websocketInstance.value = new WebSocket(`${socketUrl}?token=${token}&workflowId=${workflowId}`)

        websocketInstance.value.addEventListener('open', () => {
            if (!isCurrentSocket()) {
                return
            }

            if (timer_id) {
                clearTimeout(timer_id)
            }
            attempts = 0
            ping()
            resolve()
        })

        websocketInstance.value.addEventListener('error', () => {
            if (!isCurrentSocket()) {
                return
            }

            if (timer_id) {
                clearTimeout(timer_id)
            }
        })

        websocketInstance.value.addEventListener('close', (arg) => {
            if (!isCurrentSocket()) {
                return
            }

            if (arg.code === WEBSOCKET_CLOSED_CODE) {
                return
            }

            // eslint-disable-next-line no-console
            console.error(`Socket closed. Reconnecting - attempt: ${attempts}`)

            if (attempts >= MAX_ATTEMPTS) {
                clearTimeout(timer_id)
                reject(new WebSocketConnectionError(arg.code, arg.reason))
                return
            }

            logger.warn('WebSocket closed unexpectedly', {
                code: arg.code,
                reason: arg.reason,
                wasClean: arg.wasClean,
                attempts,
            })

            timer_id = setTimeout(() => {
                if (!isCurrentSocket()) {
                    return
                }

                attempts += 1

                resolve(initialize(token, workflowId, mode))
            }, (1000 * (attempts + 1)))
        })

        websocketInstance.value.addEventListener('message', (m) => {
            if (!isCurrentSocket()) {
                return
            }

            handleWebsocketMessage(m)
        })
    })

    const send = (message: string | ArrayBufferLike | Blob | ArrayBufferView) => {
        if (websocketInstance.value?.readyState === WEBSOCKET_STATUS.open) {
            websocketInstance.value.send(message)
        }
    }

    const reset = () => {
        generation += 1

        clearTimeout(timer_id)
        timer_id = undefined
        clearTimeout(pingTimerId)
        pingTimerId = undefined
        attempts = 0

        websocketInstance.value?.close(WEBSOCKET_CLOSED_CODE)
        websocketInstance.value = undefined

        store.reset()
    }

    return {
        store,
        websocketInstance,
        handleWebsocketMessage,
        initialize,
        ping,
        send,
        reset,
    }
}

export type WebSocketTransport = ReturnType<typeof createWebSocketTransport>
